import { motion } from "framer-motion";
import { ArrowUpRight, Play, Sparkles, Search, LineChart, MousePointer2 } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

function FloatingBlob({ className, delay = 0 }) {
  return (
    <motion.div
      aria-hidden
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5, delay }}
      className={`absolute rounded-full blur-[120px] pointer-events-none ${className}`}
    />
  );
}

function KeywordChip({ text, className, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease, delay }}
      className={`absolute hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium
        bg-white/[0.04] border border-white/10 backdrop-blur-md text-white/85 ${className}`}
    >
      <Search className="w-3 h-3 text-apex-cyan" strokeWidth={2} />
      {text}
    </motion.div>
  );
}

export default function Hero() {
  return (
    <section id="home" data-testid="hero-section" className="relative min-h-[100svh] pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden">
      {/* Background layers */}
      <div className="absolute inset-0 grid-lines opacity-40 animate-grid-pan pointer-events-none" />
      <FloatingBlob className="w-[560px] h-[560px] bg-apex-cyan/45 top-[-140px] left-[-160px] animate-blob-slow" />
      <FloatingBlob className="w-[520px] h-[520px] bg-apex-purple/45 top-[8%] right-[-160px] animate-blob-med" delay={0.2} />
      <FloatingBlob className="w-[460px] h-[460px] bg-apex-pink/35 top-[35%] left-[35%] animate-blob-slow" delay={0.35} />
      <FloatingBlob className="w-[440px] h-[440px] bg-apex-orange/35 bottom-[-160px] left-[25%] animate-blob-med" delay={0.5} />
      {/* Chromatic aurora bar */}
      <div aria-hidden className="absolute inset-x-0 top-[62%] h-[2px] bg-apex-aurora opacity-40 blur-[2px] pointer-events-none" />

      <div className="container-apex relative">
        <div className="flex flex-col items-start md:items-center text-left md:text-center max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/[0.04] border border-white/10 backdrop-blur-md text-xs font-medium text-white/80 mb-8"
            data-testid="hero-badge"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-apex-cyan opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-apex-cyan" />
            </span>
            <span data-testid="hero-legal-name" className="font-semibold text-white">APEX Innovation Solutions</span>
            <span className="text-white/40">·</span>
            Now booking Q1 growth projects
            <Sparkles className="w-3.5 h-3.5 text-apex-orange" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease, delay: 0.05 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-extrabold tracking-tight leading-[1.02]"
            data-testid="hero-headline"
          >
            Web Design & SEO built to{" "}
            <span className="relative inline-block">
              <span className="gradient-text">move your business forward</span>
              <motion.span
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.9, delay: 0.6, ease }}
                className="absolute left-0 -bottom-2 h-[3px] w-full bg-gradient-to-r from-apex-cyan via-apex-blue to-apex-orange origin-left rounded-full"
              />
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.15 }}
            className="mt-7 max-w-2xl text-base md:text-lg text-white/70 leading-relaxed"
            data-testid="hero-subheadline"
          >
            APEX creates high-converting websites, search-optimized content, and digital growth systems that help brands attract, engage, and convert better customers.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.25 }}
            className="mt-9 flex flex-col sm:flex-row items-start md:items-center gap-3"
          >
            <a href="#contact" data-testid="hero-primary-cta" className="btn-primary">
              Start Your Growth Project
              <ArrowUpRight className="w-4 h-4" strokeWidth={2.2} />
            </a>
            <a href="#work" data-testid="hero-secondary-cta" className="btn-ghost">
              <Play className="w-3.5 h-3.5" fill="currentColor" />
              View Our Work
            </a>
          </motion.div>
        </div>

        {/* Hero visual: floating cards */}
        <div className="relative mt-20 md:mt-24 max-w-6xl mx-auto">
          <div className="relative">
            {/* Central dashboard mock card */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease, delay: 0.35 }}
              className="glass glass-inner-glow p-6 md:p-8 relative overflow-hidden"
              data-testid="hero-mock-dashboard"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
                </div>
                <div className="text-[11px] tracking-[0.3em] uppercase text-white/50">apex.dashboard/growth</div>
                <div className="text-[11px] text-white/50">Live</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Metric card 1 */}
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                  <div className="text-xs uppercase tracking-widest text-white/50">Organic Traffic</div>
                  <div className="text-3xl font-heading font-bold mt-2">+142%</div>
                  <div className="flex items-center gap-1 mt-1 text-emerald-400 text-xs">
                    <LineChart className="w-3 h-3" /> vs previous 90 days
                  </div>
                  <MiniSpark />
                </div>
                {/* Metric card 2 */}
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                  <div className="text-xs uppercase tracking-widest text-white/50">Conversion Rate</div>
                  <div className="text-3xl font-heading font-bold mt-2 text-apex-orange">3.4x</div>
                  <div className="text-xs text-white/60 mt-1">from 1.1% → 3.7%</div>
                  <div className="mt-3 h-2 rounded-full bg-white/[0.06] overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: "78%" }}
                      transition={{ duration: 1.4, delay: 0.8, ease }}
                      className="h-full bg-gradient-to-r from-apex-orange via-apex-orange to-apex-cyan"
                    />
                  </div>
                </div>
                {/* Metric card 3 */}
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                  <div className="text-xs uppercase tracking-widest text-white/50">Keywords ranking</div>
                  <div className="text-3xl font-heading font-bold mt-2">1,284</div>
                  <div className="text-xs text-white/60 mt-1">312 on page 1</div>
                  <div className="flex gap-1 mt-3">
                    {[16, 22, 14, 30, 24, 34, 42, 38, 46, 55, 60].map((h, i) => (
                      <motion.div
                        key={i}
                        initial={{ height: 0 }}
                        animate={{ height: h }}
                        transition={{ duration: 0.7, delay: 0.9 + i * 0.04, ease }}
                        style={{ height: h }}
                        className="w-2 rounded-sm bg-gradient-to-t from-apex-blue to-apex-cyan"
                      />
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Floating orange arrow */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.7, ease }}
              className="absolute -top-8 -right-6 md:-top-10 md:-right-10 rotate-6"
            >
              <div className="glass px-4 py-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-apex-orange/15 border border-apex-orange/30 flex items-center justify-center">
                  <ArrowUpRight className="w-4 h-4 text-apex-orange" strokeWidth={2.4} />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-white/60">Growth</div>
                  <div className="font-heading font-bold text-lg">+65% MoM</div>
                </div>
              </div>
            </motion.div>

            {/* Floating small cards */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, delay: 0.5, ease }}
              className="absolute -bottom-8 -left-4 md:-bottom-10 md:-left-10 -rotate-3 hidden sm:block"
            >
              <div className="glass px-4 py-3 flex items-center gap-3">
                <MousePointer2 className="w-4 h-4 text-apex-cyan" />
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-white/60">CTR</div>
                  <div className="font-heading font-bold text-lg">8.9%</div>
                </div>
              </div>
            </motion.div>

            {/* Floating SEO keyword chips */}
            <KeywordChip text="web design agency" className="-top-6 left-4 md:top-2 md:-left-10" delay={0.8} />
            <KeywordChip text="conversion optimization" className="top-1/2 -right-4 md:-right-10" delay={0.9} />
            <KeywordChip text="landing page design" className="-bottom-4 left-1/3 md:bottom-2 md:left-[45%]" delay={1.0} />
          </div>
        </div>
      </div>
    </section>
  );
}

function MiniSpark() {
  return (
    <svg viewBox="0 0 120 30" className="w-full h-8 mt-3">
      <defs>
        <linearGradient id="sg" x1="0" x2="1">
          <stop offset="0%" stopColor="#23D3FF" />
          <stop offset="100%" stopColor="#246BFE" />
        </linearGradient>
      </defs>
      <motion.path
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.4, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
        d="M2,26 L14,22 L26,18 L38,20 L50,14 L62,16 L74,10 L86,8 L98,4 L118,2"
        fill="none"
        stroke="url(#sg)"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
